import { KeyValueItem, KeyValueTransformOptions, ImportResult, LossyKeyInfo } from "../types";

export function keyValueTransformer(
  parsed: Record<string, unknown>,
  existing: KeyValueItem[],
  options: KeyValueTransformOptions
): ImportResult<KeyValueItem[]> {
  const { generateId, stringify = true } = options;

  // Track existing keys for override detection
  const existingKeys = new Set(existing.map((item) => item.name));
  const overriddenKeys: string[] = [];
  const lossyKeys: LossyKeyInfo[] = [];

  // Build result map starting with existing values
  const resultMap = new Map<string, string>();
  for (const item of existing) {
    resultMap.set(item.name, item.value);
  }

  // Add parsed values, tracking overrides and lossy conversions
  for (const [key, value] of Object.entries(parsed)) {
    if (existingKeys.has(key)) {
      overriddenKeys.push(key);
    }

    // Detect lossy conversions
    if (value === null) {
      lossyKeys.push({ key, reason: "null", originalType: "null" });
    } else if (value === undefined) {
      lossyKeys.push({ key, reason: "undefined", originalType: "undefined" });
    } else if (Array.isArray(value)) {
      lossyKeys.push({ key, reason: "array", originalType: `array[${value.length}]` });
    } else if (typeof value === "object") {
      const nestedKeys = Object.keys(value as object);
      lossyKeys.push({ key, reason: "nested_object", originalType: `object{${nestedKeys.join(", ")}}` });
    }

    // Convert to string
    let stringValue: string;
    if (stringify) {
      if (value === null || value === undefined) {
        stringValue = "";
      } else if (Array.isArray(value)) {
        // For arrays, join with comma but mark as lossy
        stringValue = value.map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ");
      } else if (typeof value === "object") {
        // For objects, use JSON to preserve some structure in the string
        stringValue = JSON.stringify(value);
      } else {
        stringValue = String(value);
      }
    } else {
      stringValue = value as string;
    }

    resultMap.set(key, stringValue);
  }

  // Convert map back to array with IDs
  const items: KeyValueItem[] = Array.from(resultMap.entries()).map(([name, value]) => ({
    id: generateId(),
    name,
    value,
  }));

  return {
    success: true,
    data: items,
    overriddenKeys,
    lossyKeys: lossyKeys.length > 0 ? lossyKeys : undefined,
  };
}
